import { authFetch } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import type { Demand, DemandLine, AllocateDemandLinePayload, AllocateDailyPayload } from '@/types/demand';
import type { Absence } from '@/types';
/**
 * API Service Layer
 * 
 * Provides centralized API communication with error handling
 * for the EBScheduler application.
 */

// API Base URL - can be configured via environment variable
export const getApiBaseUrl = (): string => {
  const configuredUrl = getApiUrl();
  if (configuredUrl) {
    return configuredUrl.replace(/\/$/, '');
  }
  return import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5000';
};

/**
 * API Error class for handling API-specific errors
 */
export class ApiError extends Error {
  public statusCode: number;
  public endpoint: string;
  public details?: string;

  constructor(message: string, statusCode: number, endpoint: string, details?: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.endpoint = endpoint;
    this.details = details;
  }

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    switch (this.statusCode) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'You are not authorized. Please log in and try again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'A conflict occurred. The resource may have been modified by another user.';
      case 422:
        return 'The data provided is invalid. Please check your input.';
      case 500:
        return 'A server error occurred. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'The server is temporarily unavailable. Please try again later.';
      default:
        if (this.statusCode >= 500) {
          return 'A server error occurred. Please try again later.';
        }
        return this.message || 'An unexpected error occurred.';
    }
  }
}

/**
 * Network Error class for handling connection issues
 */
export class NetworkError extends Error {
  public endpoint: string;

  constructor(message: string, endpoint: string) {
    super(message);
    this.name = 'NetworkError';
    this.endpoint = endpoint;
  }

  getUserMessage(): string {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchWithErrorHandling<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const API_BASE_URL = getApiBaseUrl();

  const url = `${API_BASE_URL}${'/api'}${endpoint}`;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const config: RequestInit = {
    ...options,
    cache: 'no-store' as RequestCache,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await authFetch(url, config);

    const responseClone = response.clone();

    if (!response.ok) {
      let errorDetails: string | undefined;
      try {
        const errorText = await responseClone.text();
        try {
          const errorBody = JSON.parse(errorText);
          errorDetails = errorBody.message || errorBody.error || errorText;
        } catch {
          errorDetails = errorText;
        }
      } catch {
        errorDetails = response.statusText;
      }

      throw new ApiError(
        `API request failed: ${response.statusText}`,
        response.status,
        endpoint,
        errorDetails
      );
    }

    if (response.status === 204) {
      return {} as T;
    }

    const data = await response.json();
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new NetworkError('Network request failed', endpoint);
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      0,
      endpoint
    );
  }
}

// ============================================================================
// USERS API
// ============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export const usersApi = {
  getAll: () => fetchWithErrorHandling<User[]>('/users'),
  getById: (id: string) => fetchWithErrorHandling<User>(`/users/${id}`),
  create: (user: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<User>('/users', {
      method: 'POST',
      body: JSON.stringify(user),
    }),
  update: (id: string, user: Partial<User>) =>
    fetchWithErrorHandling<User>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...user, id }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/users/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// CLIENTS API
// ============================================================================

export interface Client {
  id: string;
  name: string;
  industry: string;
  streetAddress?: string;
  addressLine2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  country?: string;
  mobileNumber?: string;
  phoneNumber?: string;
  contactName?: string;
  email?: string;
  createdAt: string;
  updatedAt: string;
}

export const clientsApi = {
  getAll: (companyId?: string) => {
    const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return fetchWithErrorHandling<Client[]>(`/clients${params}`);
  },
  getById: (id: string) => fetchWithErrorHandling<Client>(`/clients/${id}`),
  create: (client: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<Client>('/clients', {
      method: 'POST',
      body: JSON.stringify(client),
    }),
  update: (id: string, client: Partial<Client>) =>
    fetchWithErrorHandling<Client>(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...client, id }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/clients/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// RESOURCES API
// ============================================================================

export interface Resource {
  id: string;
  name: string;
  role: string;
  status: string;
  type: string;
  photoUrl?: string;
  email?: string;
  phone?: string;
  department?: string;
  schedulerGroup?: string;
  schedulerGroupId?: number;
  location?: string;
  skills?: string;
  useCustomWorkingProfile?: boolean;
  customWorkingProfile?: string;
  platformUserId?: string;
  applicationRole?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceCompanyAccessDto {
  companyId: string;
  companyName: string;
  companyCode: string;
  hasAccess: boolean;
  isDefault: boolean;
}

export const resourcesApi = {
  getAll: (companyId?: string) =>
    fetchWithErrorHandling<Resource[]>(
      companyId ? `/resources?companyId=${encodeURIComponent(companyId)}` : '/resources'
    ),
  getAllForOrganisation: () =>
    fetchWithErrorHandling<Resource[]>('/resources?allCompanies=true'),
  me: () => fetchWithErrorHandling<Resource>('/resources/me'),
  getById: (id: string) => fetchWithErrorHandling<Resource>(`/resources/${id}`),
  getByStatus: (status: string) => fetchWithErrorHandling<Resource[]>(`/resources/status/${status}`),
  getByType: (type: string) => fetchWithErrorHandling<Resource[]>(`/resources/type/${type}`),
  getByDepartment: (department: string) => fetchWithErrorHandling<Resource[]>(`/resources/department/${department}`),
  getBySchedulerGroup: (schedulerGroup: string) => fetchWithErrorHandling<Resource[]>(`/resources/scheduler-group/${schedulerGroup}`),
  create: (resource: Omit<Resource, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<Resource>('/resources', {
      method: 'POST',
      body: JSON.stringify(resource),
    }),
  update: (id: string, resource: Partial<Resource>) =>
    fetchWithErrorHandling<Resource>(`/resources/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...resource, id }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/resources/${id}`, { method: 'DELETE' }),
  // Company access endpoints (for Company Access tab on Resource details)
  getCompanyAccess: (resourceId: string | number) =>
    fetchWithErrorHandling<ResourceCompanyAccessDto[]>(`/resources/${resourceId}/company-access`),
  setCompanyAccess: (resourceId: string | number, companyId: string, isDefault: boolean) =>
    fetchWithErrorHandling<void>(`/resources/${resourceId}/company-access/${companyId}`, {
      method: 'PUT',
      body: JSON.stringify({ isDefault }),
    }),
  removeCompanyAccess: (resourceId: string | number, companyId: string) =>
    fetchWithErrorHandling<void>(`/resources/${resourceId}/company-access/${companyId}`, {
      method: 'DELETE',
    }),
  setDefaultCompany: (resourceId: string | number, companyId: string) =>
    fetchWithErrorHandling<void>(`/resources/${resourceId}/company-access/${companyId}/default`, {
      method: 'PUT',
    }),
  clearDefaultCompany: (resourceId: string | number) =>
    fetchWithErrorHandling<void>(`/resources/${resourceId}/company-access/default`, {
      method: 'DELETE',
    }),
};

export interface SchedulerGroupDto {
  id: number;
  name: string;
  avatarUrl?: string;
  companyId?: string;
  resourceIds: number[];
}

export const schedulerGroupsApi = {
  getAll: (companyId?: string) =>
    fetchWithErrorHandling<SchedulerGroupDto[]>(
      companyId ? `/schedulergroups?companyId=${encodeURIComponent(companyId)}` : '/schedulergroups'
    ),
  getById: (id: number) => fetchWithErrorHandling<SchedulerGroupDto>(`/schedulergroups/${id}`),
  create: (group: Omit<SchedulerGroupDto, 'id'>) =>
    fetchWithErrorHandling<SchedulerGroupDto>('/schedulergroups', {
      method: 'POST',
      body: JSON.stringify(group),
    }),
  update: (id: number, group: Partial<SchedulerGroupDto>) =>
    fetchWithErrorHandling<SchedulerGroupDto>(`/schedulergroups/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...group, id }),
    }),
  delete: (id: number) =>
    fetchWithErrorHandling<void>(`/schedulergroups/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// COST CENTRES API
// ============================================================================

export interface CostCentre {
  id: string;
  name: string;
  code: string;
  department: string;
  status: string;
  projectCode?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const costCentresApi = {
  getAll: (companyId?: string) => {
    const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return fetchWithErrorHandling<CostCentre[]>(`/costcentres${params}`);
  },
  getById: (id: string) => fetchWithErrorHandling<CostCentre>(`/costcentres/${id}`),
  getByStatus: (status: string) => fetchWithErrorHandling<CostCentre[]>(`/costcentres/status/${status}`),
  getByDepartment: (department: string) => fetchWithErrorHandling<CostCentre[]>(`/costcentres/department/${department}`),
  create: (costCentre: Omit<CostCentre, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<CostCentre>('/costcentres', {
      method: 'POST',
      body: JSON.stringify(costCentre),
    }),
  update: (id: string, costCentre: Partial<CostCentre>) =>
    fetchWithErrorHandling<CostCentre>(`/costcentres/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...costCentre, id }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/costcentres/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// PROJECTS API
// ============================================================================

export interface ProjectFinancials {
  totalSalesBudget?: number;
  totalSalesInvoiced?: number;
  remainingSalesBudget?: number;
  totalCostsBudget?: number;
  committedCosts?: number;
  totalActualCosts?: number;
  totalCostsIncurred?: number;
  remainingCostsBudget?: number;
  budgetHours?: number;
  actualHours?: number;
  budgetLabour?: number;
  budgetExpense?: number;
  budgetRevenue?: number;
  actualLabour?: number;
  actualExpense?: number;
  actualRevenue?: number;
  completionPercent?: number;
}

export interface Project {
  id: string;
  name: string;
  clientId: string;
  clientName?: string;
  billingCode?: string;
  projectManager?: string;
  budgetDays: number;
  consumedDays: number;
  status: string;
  color: string;
  projectDescription?: string;
  startDate?: string;
  endDate?: string;
  externalId?: string;
  createdAt: string;
  updatedAt: string;
  billable?: boolean;
  financials?: ProjectFinancials;
}

// EBProjects API types (from Access Projects API via backend proxy)
export interface EBProjectsPaginationMetadata {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
}

export interface EBProjectsProject {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  code: string;
  projectManagerId: string;
  clientIds: string;
  clientNames: string;
  type: string;
  statusId: number | null;
  status: string;
  priority: string;
  health: string;
  startDate: string | null;
  endDate: string | null;
  description: string;
  location: string;
  projectOwnerId: string;
  additionalProjectManagerIds: string;
  supplierIds: string;
  currencyCode: string;
  tags: string;
  budgetLabour: number;
  budgetExpense: number;
  budgetRevenue: number;
  actualLabour: number;
  actualExpense: number;
  actualRevenue: number;
  budgetHours: number;
  actualHours: number;
  hoursVariance: number;
  hoursVariancePercent: number;
  totalBudget: number;
  totalActual: number;
  variance: number;
  variancePercent: number;
  marginPercent: number;
  completionPercent: number;
  revenueRecognitionMethod: string;
  wipAmount: number;
  unbilledAmount: number;
  billable?: boolean;
}

export interface EBProjectsPaginatedResponse {
  data: EBProjectsProject[];
  pagination: EBProjectsPaginationMetadata;
}

export interface EBProjectsCostCentre {
  id: string;
  createdAt: string;
  updatedAt: string;
  projectId: string;
  code: string;
  name: string;
  description: string;
  status?: string;
  type?: string;
  budgetAmount?: number;
  actualAmount?: number;
  committedAmount?: number;
  costsBudget?: number;
  actualCosts?: number;
  committedCosts?: number;
  revenueBudget?: number;
  actualRevenue?: number;
  hoursBudget?: number;
  actualHours?: number;
  billable?: boolean;
  isActive?: boolean;
}

export interface EBProjectsBudgetConfiguration {
  budgetMode: string;
  costBudgetEnabled: boolean;
  salesBudgetEnabled: boolean;
  resourceBudgetEnabled: boolean;
}

export interface EBProjectsCustomFieldDefinition {
  id: string;
  label: string;
  fieldType: string;
  required: boolean;
  enabled: boolean;
  displayOrder: number;
  options: string | null;
  placeholder: string | null;
}

export interface EBProjectsTimesheetEntry {
  id: string;
  weekEnding: string;
  employeeId: number;
  employeeName: string;
  department: string;
  projectCode: string;
  projectName: string;
  costCentreCode: string;
  costCentreName: string;
  description: string;
  mondayHours: number;
  tuesdayHours: number;
  wednesdayHours: number;
  thursdayHours: number;
  fridayHours: number;
  saturdayHours: number;
  sundayHours: number;
  totalHours: number;
  status: string;
  submittedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  approverName?: string;
  billable: boolean;
  hasRejections: boolean;
  rejectionCount: number;
  isOvertime: boolean;
  overtimeTypeName?: string;
  overtimeTypeMultiplier?: number;
}

export interface EBProjectsBudgetComposite {
  costBudget?: {
    id: number;
    projectId: number;
    totalBudget: number;
    totalActual: number;
    totalCommitted: number;
    totalAvailable: number;
    progressPercent: number;
  };
  salesBudget?: {
    id: number;
    projectId: number;
    projectType?: string;
    totalBudget: number;
    totalActual: number;
    totalCommitted: number;
    totalAvailable: number;
    progressPercent: number;
  };
  resourceBudget?: {
    id: number;
    projectId: number;
    totalBudget: number;
    totalActual: number;
    totalRemaining: number;
    progressPercent: number;
    billablePercent: number;
    targetBillablePercent: number;
    totalBudgetCost: number;
    totalActualCost: number;
  };
  roleBudgets: {
    id: number;
    resourceBudgetId: number;
    roleId?: number;
    roleName?: string;
    budgetHours: number;
    actualHours: number;
    remainingHours: number;
    percentUsed: number;
    billRate: number;
    budgetCost: number;
    actualCost: number;
    monthlyHours?: string;
    costCentreCode?: string;
  }[];
}

export interface EBProjectsCostCentrePaginatedResponse {
  data: EBProjectsCostCentre[];
  pagination: EBProjectsPaginationMetadata;
}

export interface EBProjectsClient {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  industry: string;
  contactName: string;
  contactEmail: string;
  logo: string;
}

export const projectsApi = {
  getAll: (companyId?: string) => {
    const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return fetchWithErrorHandling<Project[]>(`/projects${params}`);
  },
  getById: (id: string) => fetchWithErrorHandling<Project>(`/projects/${id}`),
  getByClientId: (clientId: string) => fetchWithErrorHandling<Project[]>(`/projects/client/${clientId}`),
  getByStatus: (status: string) => fetchWithErrorHandling<Project[]>(`/projects/status/${status}`),
  create: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    }),
  update: (id: string, project: Partial<Project>) =>
    fetchWithErrorHandling<Project>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...project, id }),
    }),
  updateConsumedDays: (id: string, consumedDays: number) =>
    fetchWithErrorHandling<Project>(`/projects/${id}/consumed-days`, {
      method: 'PATCH',
      body: JSON.stringify(consumedDays),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/projects/${id}`, { method: 'DELETE' }),
  // EBProjects API proxy endpoints
  getFromEBProjects: (page: number = 1, pageSize: number = 100, search?: string) => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) params.append('search', search);
    return fetchWithErrorHandling<EBProjectsPaginatedResponse>(`/projects/from-ebprojects?${params.toString()}`);
  },
  getFromEBProjectsById: (projectId: string) =>
    fetchWithErrorHandling<EBProjectsProject>(`/projects/from-ebprojects/${projectId}`),
  getCostCentresFromEBProjects: (page: number = 1, pageSize: number = 100, projectId?: string) => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (projectId) params.append('projectId', projectId);
    return fetchWithErrorHandling<EBProjectsCostCentrePaginatedResponse>(`/projects/from-ebprojects/costcentres?${params.toString()}`);
  },
  getClientsFromEBProjects: () =>
    fetchWithErrorHandling<EBProjectsClient[]>('/projects/from-ebprojects/clients'),
  getBudgetsFromEBProjects: (projectId: string) =>
    fetchWithErrorHandling<EBProjectsBudgetComposite>(`/projects/from-ebprojects/${projectId}/budgets`),
  getTimesheetsFromEBProjects: (projectId: string) =>
    fetchWithErrorHandling<EBProjectsTimesheetEntry[]>(`/projects/from-ebprojects/${projectId}/timesheets`),
  getBudgetConfigurationFromEBProjects: () =>
    fetchWithErrorHandling<EBProjectsBudgetConfiguration>('/projects/from-ebprojects/settings/budget-configuration'),
  getCustomFieldDefinitionsFromEBProjects: () =>
    fetchWithErrorHandling<EBProjectsCustomFieldDefinition[]>('/projects/from-ebprojects/settings/custom-field-definitions'),
};

// ============================================================================
// PROJECT DETAILS API
// ============================================================================

export interface ProjectDetails {
  id: string;
  projectId: string;
  name: string;
  clientId: string;
  clientName: string;
  billingCode: string;
  projectManager: string;
  color: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export const projectDetailsApi = {
  getAll: () => fetchWithErrorHandling<ProjectDetails[]>('/projectdetails'),
  getById: (id: string) => fetchWithErrorHandling<ProjectDetails>(`/projectdetails/${id}`),
  getByProjectId: (projectId: string) => fetchWithErrorHandling<ProjectDetails>(`/projectdetails/project/${projectId}`),
  getByStatus: (status: string) => fetchWithErrorHandling<ProjectDetails[]>(`/projectdetails/status/${status}`),
  create: (details: Omit<ProjectDetails, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<ProjectDetails>('/projectdetails', {
      method: 'POST',
      body: JSON.stringify(details),
    }),
  update: (id: string, details: Partial<ProjectDetails>) =>
    fetchWithErrorHandling<ProjectDetails>(`/projectdetails/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...details, id }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/projectdetails/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// BOOKING TYPES API
// ============================================================================

export interface BookingType {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: string;
  active: boolean;
  isDefault: boolean;
  sortOrder: number;
  color: string;
  requiresApproval: boolean;
  allowOverlapping: boolean;
  autoUpdateTimesheet: boolean;
  doNotUpdateTimesheetWithHours: boolean;
  obeyUserWorkingProfileAndBankHolidays: boolean;
  autoAdjustBookingForWorkingProfileAndBankHolidays: boolean;
  addDaysToEndOfBooking: boolean;
  allowRepeatBookings: boolean;
  fieldConfigurations?: string;
  fieldOrder?: string;
  tabConfigurations?: string;
  leftColumnFields?: string;
  rightColumnFields?: string;
  projectFilterConfig?: string;
  companyId?: string;
  createdAt: string;
  updatedAt: string;
}

export const bookingTypesApi = {
  getAll: (companyId?: string) =>
    fetchWithErrorHandling<BookingType[]>(
      companyId ? `/bookingtypes?companyId=${encodeURIComponent(companyId)}` : '/bookingtypes'
    ),
  getActive: (companyId?: string) =>
    fetchWithErrorHandling<BookingType[]>(
      companyId ? `/bookingtypes/active?companyId=${encodeURIComponent(companyId)}` : '/bookingtypes/active'
    ),
  getById: (id: string) => fetchWithErrorHandling<BookingType>(`/bookingtypes/${id}`),
  getByCode: (code: string) => fetchWithErrorHandling<BookingType>(`/bookingtypes/code/${code}`),
  create: (bookingType: Omit<BookingType, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<BookingType>('/bookingtypes', {
      method: 'POST',
      body: JSON.stringify(bookingType),
    }),
  update: (id: string, bookingType: Partial<BookingType>) =>
    fetchWithErrorHandling<BookingType>(`/bookingtypes/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...bookingType, id }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/bookingtypes/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// FIELD CONFIGURATIONS API
// ============================================================================

export interface FieldConfigurationApi {
  id: number;
  bookingTypeId: number;
  fieldName: string;
  fieldType: string;
  customName?: string;
  placeholder?: string;
  defaultPlaceholder?: string;
  isVisible: boolean;
  isRequired: boolean;
  isCompulsory: boolean;
  isReadOnly: boolean;
  defaultValue?: string;
  validationRules?: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const fieldConfigurationsApi = {
  getAll: () => fetchWithErrorHandling<FieldConfigurationApi[]>('/fieldconfigurations'),
  getByBookingTypeId: (bookingTypeId: number) =>
    fetchWithErrorHandling<FieldConfigurationApi[]>(`/fieldconfigurations/booking-type/${bookingTypeId}`),
  getById: (id: number) => fetchWithErrorHandling<FieldConfigurationApi>(`/fieldconfigurations/${id}`),
  create: (config: Omit<FieldConfigurationApi, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<FieldConfigurationApi>('/fieldconfigurations', {
      method: 'POST',
      body: JSON.stringify(config),
    }),
  update: (id: number, config: Partial<FieldConfigurationApi>) =>
    fetchWithErrorHandling<FieldConfigurationApi>(`/fieldconfigurations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...config, id }),
    }),
  delete: (id: number) =>
    fetchWithErrorHandling<void>(`/fieldconfigurations/${id}`, { method: 'DELETE' }),
  deleteByBookingTypeId: (bookingTypeId: number) =>
    fetchWithErrorHandling<void>(`/fieldconfigurations/booking-type/${bookingTypeId}`, { method: 'DELETE' }),
  bulkUpsert: (bookingTypeId: number, configs: Omit<FieldConfigurationApi, 'id' | 'createdAt' | 'updatedAt'>[]) =>
    fetchWithErrorHandling<FieldConfigurationApi[]>(`/fieldconfigurations/booking-type/${bookingTypeId}/bulk`, {
      method: 'PUT',
      body: JSON.stringify(configs),
    }),
};

// ============================================================================
// TAB CONFIGURATIONS API
// ============================================================================

export interface TabConfigurationApi {
  id: number;
  bookingTypeId: number;
  tabName: string;
  customName?: string;
  defaultName: string;
  isVisible: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const tabConfigurationsApi = {
  getAll: () => fetchWithErrorHandling<TabConfigurationApi[]>('/tabconfigurations'),
  getByBookingTypeId: (bookingTypeId: number) =>
    fetchWithErrorHandling<TabConfigurationApi[]>(`/tabconfigurations/booking-type/${bookingTypeId}`),
  getById: (id: number) => fetchWithErrorHandling<TabConfigurationApi>(`/tabconfigurations/${id}`),
  create: (config: Omit<TabConfigurationApi, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<TabConfigurationApi>('/tabconfigurations', {
      method: 'POST',
      body: JSON.stringify(config),
    }),
  update: (id: number, config: Partial<TabConfigurationApi>) =>
    fetchWithErrorHandling<TabConfigurationApi>(`/tabconfigurations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...config, id }),
    }),
  delete: (id: number) =>
    fetchWithErrorHandling<void>(`/tabconfigurations/${id}`, { method: 'DELETE' }),
  deleteByBookingTypeId: (bookingTypeId: number) =>
    fetchWithErrorHandling<void>(`/tabconfigurations/booking-type/${bookingTypeId}`, { method: 'DELETE' }),
  bulkUpsert: (bookingTypeId: number, configs: Omit<TabConfigurationApi, 'id' | 'createdAt' | 'updatedAt'>[]) =>
    fetchWithErrorHandling<TabConfigurationApi[]>(`/tabconfigurations/booking-type/${bookingTypeId}/bulk`, {
      method: 'PUT',
      body: JSON.stringify(configs),
    }),
};

// ============================================================================
// BOOKINGS API
// ============================================================================

export interface Booking {
  id: string;
  resourceId: string;
  resourceIds?: string;
  projectId: string;
  projectName: string;
  costCentreId?: string;
  clientId?: string;
  bookingTypeId: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  timezone: string;
  allDay: boolean;
  durationType: string;
  halfDayPeriod?: string;
  durationHours: number;
  durationDays: number;
  billingClassification: string;
  workType: string;
  status: string;
  priority: string;
  color: string;
  customer?: string;
  location?: string;
  details?: string;
  taskDescription?: string;
  bookingNotes?: string;
  notifyReminders: boolean;
  costCentreTask?: string;
  bookingReference?: string;
  recurrence?: string; // JSON string of RecurrenceSettings
  recurrenceGroupId?: string;
  isRecurrenceParent?: boolean;
  recurrencePending?: boolean;
  userChar?: string; // JSON string
  userNumber?: string; // JSON string
  userFlag?: string; // JSON string
  userDate?: string; // JSON string
  userNotes?: string; // JSON string
  userTime?: string; // JSON string
  requiredSkills?: string; // JSON string array of skill names
  requiredRole?: string; // Role name
  companyId?: string; // Company ID from company dropdown
  isDeleted: boolean;
  deletedAt?: string;
  deletedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export const bookingsApi = {
  getAll: (includeDeleted = false) =>
    fetchWithErrorHandling<Booking[]>(`/bookings?includeDeleted=${includeDeleted}`),
  getUnallocated: (includeDeleted = false, companyId?: string) =>
    fetchWithErrorHandling<Booking[]>(
      `/bookings/unallocated?includeDeleted=${includeDeleted}${companyId ? `&companyId=${encodeURIComponent(companyId)}` : ''}`
    ),
  getById: (id: number | string) => fetchWithErrorHandling<Booking>(`/bookings/${id}`),
  getByResourceId: (resourceId: number | string, includeDeleted = false) =>
    fetchWithErrorHandling<Booking[]>(`/bookings/resource/${resourceId}?includeDeleted=${includeDeleted}`),
  getByProjectId: (projectId: number | string, includeDeleted = false) =>
    fetchWithErrorHandling<Booking[]>(`/bookings/project/${projectId}?includeDeleted=${includeDeleted}`),
  getByDateRange: (startDate: string, endDate: string, includeDeleted = false, companyId?: string) =>
    fetchWithErrorHandling<Booking[]>(
      `/bookings/date-range?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&includeDeleted=${includeDeleted}${companyId ? `&companyId=${encodeURIComponent(companyId)}` : ''}`
    ),
  getByStatus: (status: string, includeDeleted = false) =>
    fetchWithErrorHandling<Booking[]>(`/bookings/status/${status}?includeDeleted=${includeDeleted}`),
  getByRecurrenceGroupId: (recurrenceGroupId: string, includeDeleted = false) =>
    fetchWithErrorHandling<Booking[]>(
      `/bookings/recurrence-group/${encodeURIComponent(recurrenceGroupId)}?includeDeleted=${includeDeleted}`
    ),
  create: (booking: Omit<Booking, 'id' | 'createdAt' | 'updatedAt' | 'isDeleted'>) =>
    fetchWithErrorHandling<Booking>('/bookings', {
      method: 'POST',
      body: JSON.stringify(booking),
    }),
  update: (id: number | string, booking: Partial<Booking>) =>
    fetchWithErrorHandling<Booking>(`/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...booking, id }),
    }),
  updateStatus: (id: number | string, status: string, updatedBy: string) =>
    fetchWithErrorHandling<Booking>(`/bookings/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, updatedBy }),
    }),
  approve: (id: number | string, approvedBy: string) =>
    fetchWithErrorHandling<Booking>(`/bookings/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approvedBy }),
    }),
  softDelete: (id: number | string, deletedBy: string) =>
    fetchWithErrorHandling<void>(`/bookings/${id}/soft?deletedBy=${encodeURIComponent(deletedBy)}`, {
      method: 'DELETE',
    }),
  hardDelete: (id: number | string) =>
    fetchWithErrorHandling<void>(`/bookings/${id}`, { method: 'DELETE' }),
  restore: (id: number | string, restoredBy: string) =>
    fetchWithErrorHandling<Booking>(`/bookings/${id}/restore`, {
      method: 'POST',
      body: JSON.stringify({ restoredBy }),
    }),
  createBatch: (bookings: Omit<Booking, 'id' | 'createdAt' | 'updatedAt' | 'isDeleted'>[]) =>
    fetchWithErrorHandling<{ created: Booking[]; errors: string[] }>('/bookings/batch', {
      method: 'POST',
      body: JSON.stringify({ bookings }),
    }),
};

// ============================================================================
// DEMANDS API
// ============================================================================

export const demandsApi = {
  getById: (id: number | string) =>
    fetchWithErrorHandling<Demand>(`/demands/${id}`),
  getByCompany: (companyId: string) =>
    fetchWithErrorHandling<Demand[]>(`/demands/by-company/${encodeURIComponent(companyId)}`),
  getByProject: (projectId: number | string) =>
    fetchWithErrorHandling<Demand[]>(`/demands/by-project/${projectId}`),
  getByExternalRequestId: (externalRequestId: string) =>
    fetchWithErrorHandling<Demand>(`/demands/by-external-request/${encodeURIComponent(externalRequestId)}`),
  getLines: (demandId: number | string) =>
    fetchWithErrorHandling<DemandLine[]>(`/demands/${demandId}/lines`),
  allocateLine: (demandId: number | string, lineId: number | string, payload: AllocateDemandLinePayload) =>
    fetchWithErrorHandling<unknown>(`/demands/${demandId}/lines/${lineId}/allocate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  allocateLineDaily: (demandId: number | string, lineId: number | string, payload: AllocateDailyPayload) =>
    fetchWithErrorHandling<unknown>(`/demands/${demandId}/lines/${lineId}/allocate-daily`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateStatus: (id: number | string, status: string) =>
    fetchWithErrorHandling<unknown>(`/demands/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
};

// ============================================================================
// RESOURCE AVAILABILITY API
// Preview-only availability breakdown — no bookings created.
// Used by the planner to get server-side daily capacity calculations.
// ============================================================================

export interface DailyAvailabilitySlot {
  date: string;
  hours: number;
  startTime: string;
  endTime: string;
  availableHours: number;
}

export interface ResourceAvailabilityQuery {
  resourceId: number;
  startDate: string;
  endDate: string;
  totalHours: number;
}

export interface PlanAvailabilityRequest {
  startDate: string;
  endDate: string;
  totalHours: number;
  role?: string;
  skills?: string[];
  location?: string;
  businessHoursStart?: number;
  businessHoursEnd?: number;
}

export interface ResourcePlanResult {
  resourceId: number;
  resourceName: string;
  resourceRole: string;
  location?: string;
  skills?: string;
  matchScore: number;
  totalAvailableHours: number;
  dailyBreakdown: DailyAvailabilitySlot[];
}

export interface PlanAvailabilityResponse {
  resources: ResourcePlanResult[];
}

export const resourceAvailabilityApi = {
  getBreakdown: (resourceId: number, payload: { startDate: string; endDate: string; totalHours: number }) =>
    fetchWithErrorHandling<DailyAvailabilitySlot[]>(`/resources/${resourceId}/availability-breakdown`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getBatchBreakdown: (queries: ResourceAvailabilityQuery[]) =>
    fetchWithErrorHandling<Record<string, DailyAvailabilitySlot[]>>(`/resources/availability-breakdown-batch`, {
      method: 'POST',
      body: JSON.stringify({ queries }),
    }),
  planAvailability: (payload: PlanAvailabilityRequest) =>
    fetchWithErrorHandling<PlanAvailabilityResponse>(`/resources/plan-availability`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// ============================================================================
// ABSENCES API
// ============================================================================

export const absencesApi = {
  getByDateRange: (startDate: string, endDate: string, companyId?: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    if (companyId) params.set('companyId', companyId);
    return fetchWithErrorHandling<Absence[]>(`/absences?${params.toString()}`);
  },
};

// ============================================================================
// FORECAST BOOKINGS API
// Dedicated API for the Utilisation Forecast report.
// Uses the same backend endpoint but is kept separate so that changes to
// the calendar date-range fetch do not affect the forecast, and vice-versa.
// ============================================================================

export const forecastBookingsApi = {
  getByDateRange: (startDate: string, endDate: string, includeDeleted = false, companyId?: string) =>
    fetchWithErrorHandling<Booking[]>(
      `/bookings/date-range?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&includeDeleted=${includeDeleted}${companyId ? `&companyId=${encodeURIComponent(companyId)}` : ''}`
    ),
};

// ============================================================================
// RECURRENCE SETTINGS API
// ============================================================================

export interface RecurrenceSettingsApi {
  id: number;
  bookingId: number;
  pattern: string;
  interval: number;
  daysOfWeek?: string;
  dayOfMonth?: number;
  weekOfMonth?: number;
  endType: string;
  endAfterOccurrences?: number;
  endDate?: string;
  excludedDates?: string;
  createdAt: string;
  updatedAt: string;
}

export const recurrenceSettingsApi = {
  getByBookingId: (bookingId: number) =>
    fetchWithErrorHandling<RecurrenceSettingsApi>(`/recurrencesettings/booking/${bookingId}`),
  getByRecurrenceGroupId: (recurrenceGroupId: string) =>
    fetchWithErrorHandling<RecurrenceSettingsApi>(`/recurrencesettings/group/${recurrenceGroupId}`),
  create: (settings: Omit<RecurrenceSettingsApi, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<RecurrenceSettingsApi>('/recurrencesettings', {
      method: 'POST',
      body: JSON.stringify(settings),
    }),
  update: (id: number, settings: RecurrenceSettingsApi) =>
    fetchWithErrorHandling<RecurrenceSettingsApi>(`/recurrencesettings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  delete: (id: number) =>
    fetchWithErrorHandling<void>(`/recurrencesettings/${id}`, {
      method: 'DELETE',
    }),
  deleteByBookingId: (bookingId: number) =>
    fetchWithErrorHandling<void>(`/recurrencesettings/booking/${bookingId}`, {
      method: 'DELETE',
    }),
};

// ============================================================================
// BOOKING HISTORY API
// ============================================================================

export interface BookingHistory {
  id: string;
  bookingId: string;
  changeType: string;
  changedFields?: string;
  previousState?: string;
  newState?: string;
  changeReason?: string;
  changeNotes?: string;
  changedAt: string;
  changedBy: string;
  changedByName: string;
  createdAt: string;
}

export const bookingHistoryApi = {
  getByBookingId: (bookingId: string) =>
    fetchWithErrorHandling<BookingHistory[]>(`/bookinghistory/booking/${bookingId}`),
  getById: (id: string) => fetchWithErrorHandling<BookingHistory>(`/bookinghistory/${id}`),
  getByChangedBy: (changedBy: string) =>
    fetchWithErrorHandling<BookingHistory[]>(`/bookinghistory/changed-by/${changedBy}`),
  getByDateRange: (startDate: string, endDate: string) =>
    fetchWithErrorHandling<BookingHistory[]>(
      `/bookinghistory/date-range?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
    ),
  create: (history: Omit<BookingHistory, 'id' | 'createdAt'>) =>
    fetchWithErrorHandling<BookingHistory>('/bookinghistory', {
      method: 'POST',
      body: JSON.stringify(history),
    }),
};

// ============================================================================
// BOOKING CONFLICTS API
// ============================================================================

export interface BookingConflict {
  id: string;
  bookingId1: string;
  bookingId2: string;
  resourceId: string;
  conflictType: string;
  conflictSeverity: string;
  overlapStartTime: string;
  overlapEndTime: string;
  overlapDurationMinutes: number;
  isResolved: boolean;
  resolutionType?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
  detectedAt: string;
  detectedBy: string;
  createdAt: string;
}

export const bookingConflictsApi = {
  getAll: () => fetchWithErrorHandling<BookingConflict[]>('/bookingconflicts'),
  getUnresolved: () => fetchWithErrorHandling<BookingConflict[]>('/bookingconflicts/unresolved'),
  getByBookingId: (bookingId: string) =>
    fetchWithErrorHandling<BookingConflict[]>(`/bookingconflicts/booking/${bookingId}`),
  getByResourceId: (resourceId: string) =>
    fetchWithErrorHandling<BookingConflict[]>(`/bookingconflicts/resource/${resourceId}`),
  getById: (id: string) => fetchWithErrorHandling<BookingConflict>(`/bookingconflicts/${id}`),
  create: (conflict: Omit<BookingConflict, 'id' | 'createdAt'>) =>
    fetchWithErrorHandling<BookingConflict>('/bookingconflicts', {
      method: 'POST',
      body: JSON.stringify(conflict),
    }),
  resolve: (id: string, resolvedBy: string, resolutionNotes?: string) =>
    fetchWithErrorHandling<BookingConflict>(`/bookingconflicts/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolvedBy, resolutionNotes }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/bookingconflicts/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// BOOKING TEMPLATES API
// ============================================================================

export interface BookingTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  bookingTypeId: string;
  templateData?: string;
  isPublic: boolean;
  isActive: boolean;
  usageCount: number;
  lastUsedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const bookingTemplatesApi = {
  getAll: () => fetchWithErrorHandling<BookingTemplate[]>('/bookingtemplates'),
  getActive: () => fetchWithErrorHandling<BookingTemplate[]>('/bookingtemplates/active'),
  getById: (id: string) => fetchWithErrorHandling<BookingTemplate>(`/bookingtemplates/${id}`),
  getByBookingTypeId: (bookingTypeId: string) =>
    fetchWithErrorHandling<BookingTemplate[]>(`/bookingtemplates/booking-type/${bookingTypeId}`),
  create: (template: Omit<BookingTemplate, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>) =>
    fetchWithErrorHandling<BookingTemplate>('/bookingtemplates', {
      method: 'POST',
      body: JSON.stringify(template),
    }),
  update: (id: string, template: Partial<BookingTemplate>) =>
    fetchWithErrorHandling<BookingTemplate>(`/bookingtemplates/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...template, id }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/bookingtemplates/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// STATUS CONFIGS API
// ============================================================================

export interface StatusConfigApi {
  id: number;
  appSettingsId: number;
  statusKey: string;
  displayName: string;
  color: string;
  isEnabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const statusConfigsApi = {
  getAll: () => fetchWithErrorHandling<StatusConfigApi[]>('/statusconfigs'),
  getByAppSettingsId: (appSettingsId: string) =>
    fetchWithErrorHandling<StatusConfigApi[]>(`/statusconfigs/app-settings/${appSettingsId}`),
  getEnabled: () => fetchWithErrorHandling<StatusConfigApi[]>('/statusconfigs/enabled'),
  getById: (id: string) => fetchWithErrorHandling<StatusConfigApi>(`/statusconfigs/${id}`),
  create: (config: Omit<StatusConfigApi, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<StatusConfigApi>('/statusconfigs', {
      method: 'POST',
      body: JSON.stringify(config),
    }),
  update: (id: number, config: Partial<StatusConfigApi>) =>
    fetchWithErrorHandling<StatusConfigApi>(`/statusconfigs/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...config, id: Number(id) }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/statusconfigs/${id}`, { method: 'DELETE' }),
  deleteByAppSettingsId: (appSettingsId: string) =>
    fetchWithErrorHandling<void>(`/statusconfigs/app-settings/${appSettingsId}`, { method: 'DELETE' }),
};

// ============================================================================
// COLOR OVERRIDES API
// ============================================================================

export interface ColorOverrideApi {
  id: number;
  appSettingsId: number;
  entityType: string;
  entityId: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export const colorOverridesApi = {
  getAll: () => fetchWithErrorHandling<ColorOverrideApi[]>('/coloroverrides'),
  getByAppSettingsId: (appSettingsId: string) =>
    fetchWithErrorHandling<ColorOverrideApi[]>(`/coloroverrides/app-settings/${appSettingsId}`),
  getById: (id: string) => fetchWithErrorHandling<ColorOverrideApi>(`/coloroverrides/${id}`),
  create: (colorOverride: Omit<ColorOverrideApi, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<ColorOverrideApi>('/coloroverrides', {
      method: 'POST',
      body: JSON.stringify(colorOverride),
    }),
  update: (id: number, colorOverride: Partial<ColorOverrideApi>) =>
    fetchWithErrorHandling<ColorOverrideApi>(`/coloroverrides/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...colorOverride, id }),
    }),
  delete: (id: number) =>
    fetchWithErrorHandling<void>(`/coloroverrides/${id}`, { method: 'DELETE' }),
  deleteByAppSettingsId: (appSettingsId: string) =>
    fetchWithErrorHandling<void>(`/coloroverrides/app-settings/${appSettingsId}`, { method: 'DELETE' }),
};

// ============================================================================
// APP SETTINGS API
// ============================================================================

export interface AppSettings {
  id: number;
  defaultCalendarView: string;
  businessHoursStart: number;
  businessHoursEnd: number;
  timeSlotInterval: number;
  showAbsence: boolean;
  absenceLabelOverride?: string;
  absenceColor: string;
  showPublicHolidays: boolean;
  publicHolidayColor: string;
  groupingMethod: string;
  confirmDragDrop: boolean;
  defaultToLoggedInResource: boolean;
  doNotCopyUserFields: boolean;
  showResourceRates: boolean;
  copyResourceRates: boolean;
  defaultTimezone: string;
  enableTimezones: boolean;
  availableTimezones?: string;
  quickAddDefault?: string;
  descriptionDefault: string;
  availableProjectStatuses?: string;
  availableCostCentreStatuses?: string;
  defaultBookingTypeId?: number;
  timelineDefaultTimeRange?: string;
  timelineDefaultBookingType?: string;
  timelineDefaultBookingStatus?: string;
  timelineDefaultView?: string;
  timelineDefaultTimezone?: string;
  calendarSyncUrl?: string;
  userConfirmDragDrop?: boolean;
  userDefaultToLoggedInResource?: boolean;
  absenceIntegrationEnabled: boolean;
  absenceApiUrl?: string;
  absenceApiKey?: string;
  absenceSyncInterval: number;
  absenceAutoSync: boolean;
  companyId?: string;
  createdAt: string;
  updatedAt: string;
}

export const appSettingsApi = {
  getAll: () => fetchWithErrorHandling<AppSettings[]>('/appsettings'),
  getById: (id: number) => fetchWithErrorHandling<AppSettings>(`/appsettings/${id}`),
  getDefault: () => fetchWithErrorHandling<AppSettings>('/appsettings/default'),
  getByCompanyId: (companyId: string) =>
    fetchWithErrorHandling<AppSettings>(`/appsettings/company/${encodeURIComponent(companyId)}`),
  create: (settings: Omit<AppSettings, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<AppSettings>('/appsettings', {
      method: 'POST',
      body: JSON.stringify(settings),
    }),
  update: (id: number, settings: Partial<AppSettings>) =>
    fetchWithErrorHandling<AppSettings>(`/appsettings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...settings, id }),
    }),
  patchByCompany: (fieldsToUpdate: Record<string, unknown>) =>
    fetchWithErrorHandling<AppSettings>('/appsettings/company', {
      method: 'PATCH',
      body: JSON.stringify(fieldsToUpdate),
    }),
  delete: (id: number) =>
    fetchWithErrorHandling<void>(`/appsettings/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// TAGS API
// ============================================================================

export interface Tag {
  id: string;
  name: string;
  color?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export const tagsApi = {
  getAll: () => fetchWithErrorHandling<Tag[]>('/tags'),
  getById: (id: string) => fetchWithErrorHandling<Tag>(`/tags/${id}`),
  getByName: (name: string) => fetchWithErrorHandling<Tag>(`/tags/name/${name}`),
  search: (term: string) => fetchWithErrorHandling<Tag[]>(`/tags/search?term=${encodeURIComponent(term)}`),
  create: (tag: Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<Tag>('/tags', {
      method: 'POST',
      body: JSON.stringify(tag),
    }),
  update: (id: string, tag: Partial<Tag>) =>
    fetchWithErrorHandling<Tag>(`/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...tag, id }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/tags/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// BOOKING METADATA API
// ============================================================================

export interface BookingMetadata {
  id: string;
  version: string;
  lastSyncAt: string;
  totalBookings: number;
  totalBookingTypes: number;
  syncStatus: string;
  syncBy?: string;
  createdAt: string;
  updatedAt: string;
}

export const bookingMetadataApi = {
  getAll: () => fetchWithErrorHandling<BookingMetadata[]>('/bookingmetadata'),
  getById: (id: string) => fetchWithErrorHandling<BookingMetadata>(`/bookingmetadata/${id}`),
  getLatest: () => fetchWithErrorHandling<BookingMetadata>('/bookingmetadata/latest'),
  create: (metadata: Omit<BookingMetadata, 'id' | 'createdAt' | 'updatedAt'>) =>
    fetchWithErrorHandling<BookingMetadata>('/bookingmetadata', {
      method: 'POST',
      body: JSON.stringify(metadata),
    }),
  update: (id: string, metadata: Partial<BookingMetadata>) =>
    fetchWithErrorHandling<BookingMetadata>(`/bookingmetadata/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...metadata, id }),
    }),
  updateSync: (id: string, syncBy: string, syncStatus: string) =>
    fetchWithErrorHandling<BookingMetadata>(`/bookingmetadata/${id}/sync`, {
      method: 'PATCH',
      body: JSON.stringify({ syncBy, syncStatus }),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/bookingmetadata/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// BOOKING RESOURCES API
// ============================================================================

export interface BookingResource {
  id: number;
  bookingId: number;
  resourceId: number;
  isPrimary: boolean;
  createdAt: string;
  resourceName?: string;
  resourceRole?: string;
}

export const bookingResourcesApi = {
  getByBookingId: (bookingId: number) =>
    fetchWithErrorHandling<BookingResource[]>(`/bookingresources/booking/${bookingId}`),
  getByResourceId: (resourceId: number) =>
    fetchWithErrorHandling<BookingResource[]>(`/bookingresources/resource/${resourceId}`),
  create: (bookingResource: Omit<BookingResource, 'id' | 'createdAt' | 'resourceName' | 'resourceRole'>) =>
    fetchWithErrorHandling<BookingResource>('/bookingresources', {
      method: 'POST',
      body: JSON.stringify(bookingResource),
    }),
  update: (id: number, bookingResource: Partial<BookingResource>) =>
    fetchWithErrorHandling<BookingResource>(`/bookingresources/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...bookingResource, id }),
    }),
  delete: (id: number) =>
    fetchWithErrorHandling<void>(`/bookingresources/${id}`, { method: 'DELETE' }),
  deleteByBookingId: (bookingId: number) =>
    fetchWithErrorHandling<void>(`/bookingresources/booking/${bookingId}`, { method: 'DELETE' }),
};

// ============================================================================
// CALENDAR SYNC API
// ============================================================================

export interface CalendarSyncLinkRequest {
  userId: string;
}

export interface CalendarSyncLinkResponse {
  icsFileUrl: string;
  fileName: string;
  platformUserId?: string;
  organisationId?: string;
}

export const calendarSyncApi = {
  /** Generates a link to download an .ics file for the current user's calendar */
  generateLink: (request: CalendarSyncLinkRequest) =>
    fetchWithErrorHandling<CalendarSyncLinkResponse>('/calendarsync/generate-link', {
      method: 'POST',
      body: JSON.stringify(request),
    }),
  /** Downloads the .ics calendar feed file */
  downloadIcsFile: async (userId: string): Promise<Blob> => {
    const API_BASE_URL = getApiBaseUrl();
    const url = `${API_BASE_URL}/api/calendarsync/download?userId=${encodeURIComponent(userId)}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new ApiError(
        `Failed to download .ics file: ${response.statusText}`,
        response.status,
        '/calendarsync/download'
      );
    }
    return response.blob();
  },
  /** Deletes the .ics calendar feed file from Azure Blob Storage */
  deleteLink: (request: CalendarSyncLinkRequest) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>('/calendarsync/delete-link', {
      method: 'DELETE',
      body: JSON.stringify(request),
    }),
  /** Manually triggers a calendar sync for a specific booking */
  enqueueSync: (bookingId: number, userId: string) =>
    fetchWithErrorHandling<{ success: boolean; message: string }>(
      `/calendarsync/enqueue/${bookingId}?userId=${encodeURIComponent(userId)}`,
      { method: 'POST' }
    ),
};

// ============================================================================
// INTEGRATIONS API (Companies, ProductTypes, ProductInstances)
// ============================================================================

export interface CompanyDto {
  id: string;
  organisationId: string;
  name: string;
  code: string;
  status: string;
  description: string | null;
  settings: string | null;
  employeeCount: number;
  userAccessCount: number;
  activeProjects: number;
  financialIntegrationStatus: string;
  hrIntegrationStatus: string;
  projectIntegrationStatus: string;
  plannerIntegrationStatus: string;
  productInstances: ProductInstanceDto[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductTypeDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
  icon: string | null;
  features: string[];
  requiresProducts: string[] | null;
  displayOrder: number;
  platformProductId: string | null;
}

export interface ProductInstanceDto {
  id: string;
  companyId: string;
  productTypeCode: string;
  productTypeName: string;
  productTypeCategory: string;
  productTypeIcon: string | null;
  instanceName: string;
  connectionStatus: string;
  apiEndpoint: string | null;
  lastSync: string | null;
  lastTestDate: string | null;
  lastTestStatus: string | null;
  syncFrequency: string;
  autoSync: boolean;
  config: string | null;
  errorMessage: string | null;
  autoConnectedByInstanceId: string | null;
  externalInstanceId: string | null;
  externalInstanceIdSecondary: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExternalInstanceDto {
  id: string;
  name: string;
  productId: string;
  isActive: boolean;
}

export interface AccessProjectsCompanyDto {
  id: string;
  name: string;
}

export interface AvailableDatabaseDto {
  id: string;
  label: string;
  environment?: string;
}

export interface TestConnectionResultDto {
  success: boolean;
  message: string;
  testedAt: string;
}

export interface SyncResultDto {
  success: boolean;
  message: string;
  syncedAt: string | null;
}

export interface TestIntegrationResponseDto {
  success: boolean;
  message: string;
  testedAt: string;
}

export interface SyncIntegrationResponseDto {
  success: boolean;
  message: string;
  syncedAt: string | null;
  updatedCompany: CompanyDto | undefined;
}

export interface CompanyUserDto {
  id: string;
  name: string;
  email: string;
  role: string;
  photoUrl?: string;
}

export const companiesApi = {
  getAll: () => fetchWithErrorHandling<CompanyDto[]>('/companies'),
  getMyAccess: () => fetchWithErrorHandling<(string | { companyId: string; isDefault: boolean })[]>('/companies/my-access'),
  getById: (id: string) => fetchWithErrorHandling<CompanyDto>(`/companies/${id}`),
  getByCode: (code: string) => fetchWithErrorHandling<CompanyDto>(`/companies/by-code/${code}`),
  create: (data: { name: string; code: string; status: string }) =>
    fetchWithErrorHandling<CompanyDto>('/companies', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: { name?: string; code?: string; status?: string }) =>
    fetchWithErrorHandling<CompanyDto>(`/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchWithErrorHandling<void>(`/companies/${id}`, { method: 'DELETE' }),
  testIntegration: (id: string, type: string) =>
    fetchWithErrorHandling<TestIntegrationResponseDto>(`/companies/${id}/integrations/test`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    }),
  syncIntegration: (id: string, type: string) =>
    fetchWithErrorHandling<SyncIntegrationResponseDto>(`/companies/${id}/integrations/sync`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    }),
  resyncIntegration: (id: string, type: string) =>
    fetchWithErrorHandling<SyncIntegrationResponseDto>(`/companies/${id}/integrations/resync`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    }),
  // Company user access endpoints
  getUsers: (companyId: string) =>
    fetchWithErrorHandling<CompanyUserDto[]>(`/companies/${companyId}/users`),
  addUser: (companyId: string, resourceId: number) =>
    fetchWithErrorHandling<CompanyUserDto>(`/companies/${companyId}/users`, {
      method: 'POST',
      body: JSON.stringify({ resourceId }),
    }),
  removeUser: (companyId: string, resourceId: number) =>
    fetchWithErrorHandling<void>(`/companies/${companyId}/users/${resourceId}`, {
      method: 'DELETE',
    }),
  searchUsers: (companyId: string, query: string) =>
    fetchWithErrorHandling<CompanyUserDto[]>(`/companies/${companyId}/users/search?query=${encodeURIComponent(query)}`),
  getApiSecret: (companyId: string, type: string) =>
    fetchWithErrorHandling<{ maskedApiSecret: string | null }>(`/companies/${companyId}/integrations/${type}/api-secret`),
  saveApiSecret: (companyId: string, type: string, apiSecret: string) =>
    fetchWithErrorHandling<{ maskedApiSecret: string | null }>(`/companies/${companyId}/integrations/${type}/api-secret`, {
      method: 'PUT',
      body: JSON.stringify({ apiSecret }),
    }),
  deleteApiSecret: (companyId: string, type: string) =>
    fetchWithErrorHandling<void>(`/companies/${companyId}/integrations/${type}/api-secret`, {
      method: 'DELETE',
    }),
  removeIntegration: (companyId: string, type: string) =>
    fetchWithErrorHandling<CompanyDto>(`/companies/${companyId}/integrations/${type}`, {
      method: 'DELETE',
    }),
};

export const productTypesApi = {
  getAll: () => fetchWithErrorHandling<ProductTypeDto[]>('/producttypes'),
  getById: (id: string) => fetchWithErrorHandling<ProductTypeDto>(`/producttypes/${id}`),
  getByCode: (code: string) => fetchWithErrorHandling<ProductTypeDto>(`/producttypes/by-code/${code}`),
  getByCategory: (category: string) => fetchWithErrorHandling<ProductTypeDto[]>(`/producttypes/by-category/${category}`),
  getExternalInstances: (productCode: string, companyId?: string) => {
    const query = companyId ? `?requestingCompanyId=${encodeURIComponent(companyId)}` : '';
    return fetchWithErrorHandling<ExternalInstanceDto[]>(`/producttypes/${productCode}/external-instances${query}`);
  },
};

export const integrationsApi = {
  getAccessProjectsCompanies: (options?: { instanceId?: string; product?: string }) => {
    const params = new URLSearchParams();
    if (options?.instanceId) params.set('instanceId', options.instanceId);
    if (options?.product) params.set('product', options.product);
    const qs = params.toString();
    return fetchWithErrorHandling<AccessProjectsCompanyDto[]>(
      `/integrations/access-projects/companies${qs ? `?${qs}` : ''}`
    );
  },
  getAvailableDatabases: (product: string) =>
    fetchWithErrorHandling<AvailableDatabaseDto[]>(
      `/integrations/databases?product=${encodeURIComponent(product)}`
    ),
};

export const productInstancesApi = {
  getByCompany: (companyId: string) =>
    fetchWithErrorHandling<ProductInstanceDto[]>(`/companies/${companyId}/product-instances`),
  getById: (companyId: string, instanceId: string) =>
    fetchWithErrorHandling<ProductInstanceDto>(`/companies/${companyId}/product-instances/${instanceId}`),
  create: (companyId: string, data: { productType: string; instanceName: string; externalInstanceId?: string; externalInstanceIdSecondary?: string; apiEndpoint?: string }) =>
    fetchWithErrorHandling<ProductInstanceDto>(`/companies/${companyId}/product-instances`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (companyId: string, instanceId: string, data: { instanceName?: string; externalInstanceId?: string; externalInstanceIdSecondary?: string; apiEndpoint?: string; syncFrequency?: string; autoSync?: boolean }) =>
    fetchWithErrorHandling<ProductInstanceDto>(`/companies/${companyId}/product-instances/${instanceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (companyId: string, instanceId: string) =>
    fetchWithErrorHandling<void>(`/companies/${companyId}/product-instances/${instanceId}`, { method: 'DELETE' }),
  testConnection: (companyId: string, instanceId: string) =>
    fetchWithErrorHandling<TestConnectionResultDto>(`/companies/${companyId}/product-instances/${instanceId}/test-connection`, {
      method: 'POST',
    }),
  sync: (companyId: string, instanceId: string) =>
    fetchWithErrorHandling<SyncResultDto>(`/companies/${companyId}/product-instances/${instanceId}/sync`, {
      method: 'POST',
    }),
};

// ============================================================================
// BOOKING TYPE USAGES API
// ============================================================================

export interface BookingTypeUsageDto {
  id: number;
  resourceId: number;
  bookingTypeId: number;
  isReadOnly: boolean;
  isFullAccess: boolean;
  createdAt: string;
  updatedAt: string;
}

export const bookingTypeUsagesApi = {
  getAll: () => fetchWithErrorHandling<BookingTypeUsageDto[]>('/bookingtypeusages'),
  getByResourceId: (resourceId: number) =>
    fetchWithErrorHandling<BookingTypeUsageDto[]>(`/bookingtypeusages/resource/${resourceId}`),
  getByBookingTypeId: (bookingTypeId: number) =>
    fetchWithErrorHandling<BookingTypeUsageDto[]>(`/bookingtypeusages/bookingtype/${bookingTypeId}`),
  upsert: (data: { resourceId: number; bookingTypeId: number; isReadOnly: boolean; isFullAccess: boolean }) =>
    fetchWithErrorHandling<BookingTypeUsageDto>('/bookingtypeusages', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  bulkUpsert: (data: { resourceId: number; bookingTypeId: number; isReadOnly: boolean; isFullAccess: boolean }[]) =>
    fetchWithErrorHandling<BookingTypeUsageDto[]>('/bookingtypeusages/bulk', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: number) =>
    fetchWithErrorHandling<void>(`/bookingtypeusages/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// DASHBOARD API
// ============================================================================

export interface DashboardOpenDemandDto {
  total: number;
  critical: number;
  soon: number;
  upcoming: number;
}

export interface DashboardBurnoutRiskDto {
  total: number;
  critical: number;
  high: number;
}

export interface DashboardBudgetHealthDto {
  percentOnTrack: number;
  onTrack: number;
  atRisk: number;
  over: number;
}

export interface DashboardWeeklyUtilisationDto {
  week: string;
  pct: number;
}

export interface DashboardProjectAttentionDto {
  name: string;
  status: string;
  pct: number;
}

export interface DashboardOverloadedResourceDto {
  name: string;
  role: string;
  pct: number;
}

export interface DashboardSummaryDto {
  totalResources: number;
  teamUtilisation: number;
  openDemand: DashboardOpenDemandDto;
  burnoutRisk: DashboardBurnoutRiskDto;
  budgetHealth: DashboardBudgetHealthDto;
  forecastUtilisation: number;
  availableCapacityHours: number;
  benchResources: number;
  comingFreeResources: number;
  utilisationTrend: DashboardWeeklyUtilisationDto[];
  projectsRequiringAttention: DashboardProjectAttentionDto[];
  overloadedResources: DashboardOverloadedResourceDto[];
}

export const dashboardApi = {
  getSummary: (companyId?: string) => {
    const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return fetchWithErrorHandling<DashboardSummaryDto>(`/dashboard/summary${params}`);
  },
};

// ============================================================================
// EXPORT ALL APIs
// ============================================================================

export const api = {
  users: usersApi,
  clients: clientsApi,
  resources: resourcesApi,
  costCentres: costCentresApi,
  projects: projectsApi,
  projectDetails: projectDetailsApi,
  bookingTypes: bookingTypesApi,
  fieldConfigurations: fieldConfigurationsApi,
  tabConfigurations: tabConfigurationsApi,
  bookings: bookingsApi,
  bookingHistory: bookingHistoryApi,
  bookingConflicts: bookingConflictsApi,
  bookingTemplates: bookingTemplatesApi,
  statusConfigs: statusConfigsApi,
  appSettings: appSettingsApi,
  tags: tagsApi,
  bookingMetadata: bookingMetadataApi,
  bookingResources: bookingResourcesApi,
  calendarSync: calendarSyncApi,
  recurrenceSettings: recurrenceSettingsApi,
  companies: companiesApi,
  productTypes: productTypesApi,
  productInstances: productInstancesApi,
  schedulerGroups: schedulerGroupsApi,
  bookingTypeUsages: bookingTypeUsagesApi,
  integrations: integrationsApi,
  demands: demandsApi,
  absences: absencesApi,
  resourceAvailability: resourceAvailabilityApi,
  dashboard: dashboardApi,
};

export default api;
